import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Icon,
  Input,
  PortalLogo,
  SystemMessage,
} from 'vet-bones/design-system/components';

import { Loading } from 'src/ui/components/Loading';
import {
  LOGIN_AUTH0,
  LOGIN_BACK,
  LOGIN_CARD,
  LOGIN_LOOKUP_ERROR,
  LOGIN_ORG_INPUT,
  LOGIN_ORG_PROVIDER_CARD,
  LOGIN_PROVIDER_BUTTON,
  LOGIN_SSO_SUBMIT,
  LOGIN_URL_ERROR,
} from 'src/ui/constants/testIds';
import { ConnectionStatus } from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  AzureProvider,
  OktaProvider,
  PingProvider,
  useGetCustomerSsoSettingsQuery,
  useProfileQuery,
} from 'src/ui/graph/types';
import { components } from 'src/ui/templates/copy';
import microsoftIcon from 'src/ui/templates/images/microsoft-entra-icon.svg';
import oktaIcon from 'src/ui/templates/images/okta-icon.svg';
import { getConnectionFromCookie } from 'src/ui/utils/cookies/getConnectionFromCookie';
import {
  doAuth0Redirect,
  doCustomerAuthRedirect,
  redirectToUrl,
} from 'src/ui/utils/window';

const { loginPage: copy } = components;

type TParams = { customer?: string };

interface OrgInfo {
  name: string;
  domain: string;
  providerType: string;
}

const PROVIDER_CONFIG: Record<string, { label: string; icon: string }> = {
  microsoft: { label: copy.providers.microsoft, icon: microsoftIcon },
  okta: { label: copy.providers.okta, icon: oktaIcon },
};

const LoginOrgProviderCard: React.VFC<{
  org: OrgInfo;
  onBack: () => void;
}> = ({ org, onBack }) => {
  const config = PROVIDER_CONFIG[org.providerType];
  const providerLabel = config?.label || org.providerType;
  const providerIcon = config?.icon;

  return (
    <div className="sd-auth-card">
      <div className="sd-auth-card__card">
        <div className="sd-auth-card__header">
          <Icon IconComponent={PortalLogo} size={60} />
          <span>STARDOG</span>
        </div>

        <div className="sd-auth-card__body">
          <Body
            className="sd-auth-card__title"
            size="lg"
            testId={LOGIN_ORG_PROVIDER_CARD}
          >
            {copy.org.title(org.name)}
          </Body>

          <Button
            className="sd-login__provider-button"
            onClick={() =>
              redirectToUrl(`/oauth/o/${encodeURIComponent(org.domain)}/login`)
            }
            size="lg"
            testId={LOGIN_PROVIDER_BUTTON}
            variant="tertiary-accent"
          >
            {providerIcon ? (
              <img
                alt={providerLabel}
                className="sd-login__provider-icon"
                src={providerIcon}
              />
            ) : null}
            {copy.org.providerButton(providerLabel)}
          </Button>

          <Button
            className="sd-login__back-link"
            onClick={onBack}
            size="lg"
            testId={LOGIN_BACK}
            variant="ghost-gray"
          >
            {copy.org.back}
          </Button>
        </div>
      </div>

      <p className="sd-login__footer">
        {copy.main.footerPrompt}{' '}
        <a
          href="/login?signup=true&direct"
          onClick={(e) => {
            e.preventDefault();
            doAuth0Redirect({ signup: true });
          }}
        >
          {copy.main.footerLink}
        </a>
      </p>
    </div>
  );
};

const getErrorFromUrl = (): string => {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('error');
  if (encoded) {
    try {
      return atob(encoded);
    } catch {
      return encoded;
    }
  }
  return '';
};

const LoginCard: React.VFC = () => {
  const [orgDomain, setOrgDomain] = React.useState('');
  const [orgInfo, setOrgInfo] = React.useState<OrgInfo | null>(null);
  const [urlError] = React.useState(getErrorFromUrl);
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const handleOrgLookup = async () => {
    const domain = orgDomain.trim();
    if (!domain) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const res = await fetch(`/oauth/o/${encodeURIComponent(domain)}/info`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || copy.errors.orgLookupFailed);
        return;
      }
      setOrgInfo(data);
    } catch {
      setError(copy.errors.orgLookupFailed);
    } finally {
      setIsLoading(false);
    }
  };

  if (orgInfo) {
    return (
      <LoginOrgProviderCard
        onBack={() => {
          setOrgInfo(null);
          setError('');
        }}
        org={orgInfo}
      />
    );
  }

  const submitDisabled = !orgDomain.trim() || isLoading;

  return (
    <div className="sd-auth-card">
      <div className="sd-auth-card__card">
        <div className="sd-auth-card__header">
          <Icon IconComponent={PortalLogo} size={60} />
          <span>STARDOG</span>
        </div>

        <div className="sd-auth-card__body">
          {urlError ? (
            <SystemMessage
              className="sd-login__error-banner"
              testId={LOGIN_URL_ERROR}
              text={urlError}
              variant="alert"
            />
          ) : null}

          {error ? (
            <SystemMessage
              className="sd-login__error-banner"
              testId={LOGIN_LOOKUP_ERROR}
              text={error}
              variant="alert"
            />
          ) : null}

          <Body className="sd-auth-card__title" size="lg" testId={LOGIN_CARD}>
            {copy.main.title}
          </Body>

          <Button
            className="sd-login__auth0-button"
            onClick={() => doAuth0Redirect()}
            size="lg"
            testId={LOGIN_AUTH0}
            variant="primary-accent"
          >
            {copy.main.auth0Button}
          </Button>

          <div className="sd-login__divider">
            <hr />
            <span>{copy.main.divider}</span>
            <hr />
          </div>

          <Input
            className="sd-login__sso-input"
            clearable={false}
            id="org-domain-input"
            label={copy.main.ssoLabel}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOrgDomain(e.target.value);
              setError('');
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && !submitDisabled) {
                e.preventDefault();
                handleOrgLookup();
              }
            }}
            placeholder={copy.main.ssoPlaceholder}
            size="md"
            testId={LOGIN_ORG_INPUT}
            type="text"
            value={orgDomain}
          />
          <Button
            className="sd-login__sso-submit"
            disabled={submitDisabled}
            onClick={handleOrgLookup}
            size="lg"
            testId={LOGIN_SSO_SUBMIT}
            variant="tertiary-accent"
          >
            {isLoading ? copy.main.ssoSubmitLoading : copy.main.ssoSubmit}
          </Button>
        </div>
      </div>

      <p className="sd-login__footer">
        {copy.main.footerPrompt}{' '}
        <a
          href="/login?signup=true&direct"
          onClick={(e) => {
            e.preventDefault();
            doAuth0Redirect({ signup: true });
          }}
        >
          {copy.main.footerLink}
        </a>
      </p>
    </div>
  );
};

export const Login: React.VFC = () => {
  const params = useParams<TParams>();
  const { customer } = params;
  const {
    data: profileData,
    isLoading: profileDataIsLoading,
  } = useProfileQuery(graphQLClient);
  const {
    data: customerAuth,
    isLoading: customerAuthIsLoading,
  } = useGetCustomerSsoSettingsQuery(graphQLClient);

  function handleAuthRedirect(
    customer: string | undefined,
    providers: Array<PingProvider | AzureProvider | OktaProvider | null>,
    providerType: 'azure' | 'ping' | 'okta'
  ): boolean {
    if (
      customer &&
      providers.find(
        (provider) =>
          provider?.customerName.toLowerCase() === customer.toLowerCase()
      )
    ) {
      doCustomerAuthRedirect(customer, providerType);
      return true;
    }
    return false;
  }

  const [showLoginCard, setShowLoginCard] = React.useState(false);

  React.useEffect(() => {
    if (
      profileDataIsLoading ||
      !profileData ||
      !profileData.profile ||
      customerAuthIsLoading ||
      !customerAuth ||
      !customerAuth.customerSsoSettings
    ) {
      return;
    }
    const {
      azureProviders,
      pingProviders,
      oktaProviders,
    } = customerAuth.customerSsoSettings;

    const connection = getConnectionFromCookie();

    // If the user is authenticated don't ask them to login again
    // but there is probably an issue with the next param, just redirect
    // to the root application.
    if (
      profileData.profile.is_authenticated &&
      connection.status === ConnectionStatus.CONNECTED
    ) {
      redirectToUrl('/');
      return;
    }
    if (
      handleAuthRedirect(customer, azureProviders, 'azure') ||
      handleAuthRedirect(customer, pingProviders, 'ping') ||
      handleAuthRedirect(customer, oktaProviders, 'okta')
    ) {
      return;
    }

    if (profileData.profile.is_authenticated) {
      redirectToUrl('/');
      return;
    }
    setShowLoginCard(true);
  }, [
    profileData,
    customer,
    profileDataIsLoading,
    customerAuth,
    customerAuthIsLoading,
  ]);

  if (showLoginCard) {
    return <LoginCard />;
  }

  return <Loading />;
};
